// Sidebar navigation: the primary/secondary role merge, plus the mnemonic
// letter each destination owns.
//
// Extracted from Layout so the merge has one implementation and the mnemonic
// registry sees exactly the destinations the sidebar renders — a user can never
// be shown a mnemonic for a route their role cannot reach.

// `machine` is the role; `machine_man` was renamed when individual operator
// accounts became machine accounts and nothing migrated the existing rows, so
// both spellings are still in the database and both have to be honoured.
export const MACHINE_OPERATOR_ROLES = ['machine', 'machine_man', 'supervisor', 'binder'];

/**
 * Letters are assigned per destination and are unique within every single
 * role's menu. A user holding two roles can produce a duplicate; the registry resolves
 * to the primary role's entry and the loser simply renders no underline, so
 * nothing on screen ever claims a key it doesn't own.
 */
export const NAV_MNEMONICS = {
  '/': 'd',
  '/orders': 'o',
  '/customers': 'u',
  '/users': 'e',
  '/machines': 'm',
  '/catalog': 'g',
  '/design-queue': 'q',
  '/my-designs': 'n',
  '/job-cards': 'j',
  '/production': 'p',
  '/my-production-queue': 'w',
  '/plates': 'l',
  '/inventory': 'i',
  '/dispatch': 'h',
  '/payments': 'y',
  '/costing': 'b',
  '/reports': 'r',
  '/external-processing': 'x',
  '/vendors': 'v',
  '/admin-settings': 's',
  '/recycle-bin': 'z',
  '/supervisor': 'f',
};

export const navMnemonic = (path) => NAV_MNEMONICS[path];

/**
 * Which lane of the business each destination belongs to.
 *
 * Nineteen flat items is a list you read rather than a menu you aim at. The
 * lanes are the order work actually moves in — take it, make it, ship it, bill
 * it — with the reference data and the setup screens after, because those are
 * looked up rather than worked through.
 */
export const NAV_SECTIONS = [
  { id: 'work', label: 'Work', paths: [
    '/', '/supervisor',
    '/orders',
    '/design-queue', '/my-designs', '/plates',
    '/job-cards',
    '/production', '/my-production-queue',
  ] },
  { id: 'out', label: 'Out the door', paths: ['/dispatch', '/external-processing'] },
  { id: 'money', label: 'Money', paths: ['/payments', '/costing', '/reports'] },
  { id: 'directory', label: 'Directory', paths: ['/customers', '/catalog', '/inventory', '/vendors'] },
  { id: 'setup', label: 'Setup', paths: ['/users', '/machines', '/admin-settings', '/recycle-bin'] },
];

const SECTION_OF = NAV_SECTIONS.reduce((map, section) => {
  for (const path of section.paths) map[path] = section.id;
  return map;
}, {});

/**
 * Collect a flat nav list into its lanes.
 *
 * Bucketed by lane, not by adjacency — a role's menu lists Orders before
 * Customers before Users, and grouping as you walk that order produces
 * Work / Directory / Setup / Directory / Work, which is worse than no grouping
 * at all. Lanes come out in NAV_SECTIONS order; items keep their order within a
 * lane; anything with no lane collects under "More" at the end.
 *
 * The secondary-role separator splits the list first, so a secondary role's
 * destinations stay grouped behind their own divider rather than merging into
 * the primary role's lanes.
 */
function bucketIntoLanes(items) {
  const byLane = new Map();
  for (const item of items) {
    const laneId = SECTION_OF[item.path] || 'more';
    if (!byLane.has(laneId)) byLane.set(laneId, []);
    byLane.get(laneId).push(item);
  }

  const lanes = [];
  for (const section of NAV_SECTIONS) {
    const laneItems = byLane.get(section.id);
    if (laneItems?.length) lanes.push({ id: section.id, label: section.label, items: laneItems });
  }
  const rest = byLane.get('more');
  if (rest?.length) lanes.push({ id: 'more', label: 'More', items: rest });
  return lanes;
}

export function groupNavItems(items) {
  const splitAt = items.findIndex(item => item.separator);
  if (splitAt === -1) return bucketIntoLanes(items);

  return [
    ...bucketIntoLanes(items.slice(0, splitAt)),
    { id: 'separator', separator: true, items: [] },
    ...bucketIntoLanes(items.slice(splitAt + 1)),
  ];
}

/**
 * Primary-role items first, then any destinations the user's secondary roles
 * add, deduplicated by path and separated by a divider.
 */
export function buildNavItems(user, roleNav, extraItem) {
  if (!user) return roleNav.receptionist || [];

  const allRoles = user.roles || [user.role];
  const primaryItems = roleNav[user.role] || [];
  const seenPaths = new Set(primaryItems.map((item) => item.path));

  const secondaryItems = [];
  for (const role of allRoles.filter((r) => r !== user.role)) {
    for (const item of roleNav[role] || []) {
      if (!seenPaths.has(item.path)) {
        seenPaths.add(item.path);
        secondaryItems.push(item);
      }
    }
  }

  const hasMachineRole = allRoles.some((r) => MACHINE_OPERATOR_ROLES.includes(r));
  if (hasMachineRole && extraItem && !seenPaths.has(extraItem.path)) {
    secondaryItems.push(extraItem);
  }

  if (secondaryItems.length > 0) {
    return [...primaryItems, { separator: true }, ...secondaryItems];
  }
  return primaryItems;
}
